/**
 * Intent-driven push subscription.
 *
 * Buttons rendered by templates/_components/_push-subscribe-button.twig opt the
 * visitor in on click, so the browser's permission prompt only ever appears as a
 * response to a deliberate action — never on page load.
 *
 * Tagging: a CTA subscribes to one topic (`theme_<slug>` / `petition_<id>`), while the
 * notifyButton bell means "everything", which is recorded as `all_topics`. Sends filter
 * on the entry's topics OR `all_topics`, so a bell subscriber keeps receiving
 * everything — without the bell tag they would match no filter and silently get
 * nothing.
 */

const SELECTOR = '[data-push-subscribe]';
const ALL_TOPICS_TAG = 'all_topics';

const STATE = {
  unsupported: 'unsupported',
  blocked: 'blocked',
  subscribed: 'subscribed',
  idle: 'idle',
};

// Set while a CTA click is in flight, so the subscription-change handler knows the
// opt-in came from a topic button rather than the bell.
let pendingTopic = null;

function withOneSignal(callback) {
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(callback);
}

function stateText(button, state) {
  if (state === STATE.subscribed) {
    return button.dataset.pushLabelSubscribed || '';
  }

  if (state === STATE.blocked) {
    return button.dataset.pushLabelBlocked || '';
  }

  return button.dataset.pushLabelIdle || '';
}

/**
 * One string per state, used as the tooltip, the accessible name and — in the label
 * variant, which is the only one with a `[data-push-label]` span — the visible text.
 * Written into that span rather than the button so the bell icon survives.
 */
function render(button, state) {
  const text = stateText(button, state);
  const label = button.querySelector('[data-push-label]');

  button.dataset.pushState = state;
  button.dataset.pushTooltip = text;
  button.setAttribute('aria-label', text);

  if (label) {
    label.textContent = text;
  }

  // Only a browser-level block makes the button inert — following and unfollowing are
  // both actions. `aria-disabled` rather than `disabled`: browsers don't dispatch mouse
  // events to a disabled control, and in the icon variant the tooltip is the only place
  // the state is written down. Clicks are ignored in the handler instead.
  button.setAttribute('aria-disabled', String(state === STATE.blocked));
}

function isInert(button) {
  return button.getAttribute('aria-disabled') === 'true' || button.dataset.pushBusy === 'true';
}

async function currentState(OneSignal, tag) {
  if (OneSignal.Notifications.permission === false && OneSignal.Notifications.permissionNative === 'denied') {
    return STATE.blocked;
  }

  if (!OneSignal.User.PushSubscription.optedIn) {
    return STATE.idle;
  }

  // Deliberately only this topic's own tag, not `all_topics`. A bell subscriber receives
  // everything, but they don't hold this tag — and if the button showed them as
  // following it, unfollowing would have to strip `all_topics` to mean anything, which
  // would quietly unsubscribe them from every other topic as well. The bell stays the
  // control for "everything"; this button owns one tag and nothing else.
  const tags = (await OneSignal.User.getTags()) || {};

  return tags[tag] === '1' ? STATE.subscribed : STATE.idle;
}

/**
 * Waits for OneSignal to hand back a subscription id after opt-in.
 *
 * `optedIn` flips to true as soon as the SDK accepts the intent, before the browser has
 * actually registered with the push service — so it is not on its own proof that a
 * subscription exists. Without this wait the button can announce "you'll receive
 * updates" when nothing was registered and no tag will ever reach OneSignal.
 */
async function waitForSubscriptionId(OneSignal, timeout = 8000) {
  const deadline = Date.now() + timeout;

  while (Date.now() < deadline) {
    if (OneSignal.User.PushSubscription.id) {
      return OneSignal.User.PushSubscription.id;
    }

    await new Promise((resolve) => setTimeout(resolve, 250));
  }

  return null;
}

async function subscribe(OneSignal, button, tag) {
  pendingTopic = tag;

  try {
    if (!OneSignal.Notifications.permission) {
      // Only ever called from a click handler — this is the intent-driven prompt.
      await OneSignal.Notifications.requestPermission();
    }

    if (!OneSignal.Notifications.permission) {
      // Dismissing the prompt leaves permission at "default", which is not the same as
      // a block — keep the button usable so the visitor can try again.
      render(button, OneSignal.Notifications.permissionNative === 'denied' ? STATE.blocked : STATE.idle);
      return;
    }

    if (!OneSignal.User.PushSubscription.optedIn) {
      await OneSignal.User.PushSubscription.optIn();
    }

    if (!(await waitForSubscriptionId(OneSignal))) {
      // Permission is granted but no subscription materialised (no service worker, a
      // blocked push service, a private window). Tagging now would only write to the
      // local cache, so don't claim success.
      render(button, STATE.idle);
      return;
    }

    await OneSignal.User.addTag(tag, '1');
    render(button, STATE.subscribed);
  } catch (error) {
    render(button, STATE.idle);
    throw error;
  } finally {
    pendingTopic = null;
  }
}

/**
 * Stops notifications for one topic by removing its tag.
 *
 * Only that tag: the push subscription itself stays, so unfollowing one theme never
 * silently ends the visitor's other subscriptions. `all_topics` is left alone for the
 * reason given in currentState().
 */
async function unsubscribe(OneSignal, button, tag) {
  try {
    await OneSignal.User.removeTag(tag);
    render(button, STATE.idle);
  } catch (error) {
    render(button, STATE.subscribed);
    throw error;
  }
}

export function initPushSubscribe() {
  const buttons = Array.from(document.querySelectorAll(SELECTOR));

  if (!buttons.length && !window.OneSignalDeferred) {
    return;
  }

  withOneSignal(async (OneSignal) => {
    // A bell opt-in carries no topic, so tag it as following everything. Guarded by
    // pendingTopic so a CTA opt-in doesn't also get the catch-all tag.
    OneSignal.User.PushSubscription.addEventListener('change', async (event) => {
      if (!event.current.optedIn || pendingTopic) {
        return;
      }

      const tags = (await OneSignal.User.getTags()) || {};

      if (tags[ALL_TOPICS_TAG] !== '1') {
        await OneSignal.User.addTag(ALL_TOPICS_TAG, '1');
      }
    });

    for (const button of buttons) {
      const tag = button.dataset.pushTag;

      if (!tag) {
        continue;
      }

      render(button, await currentState(OneSignal, tag));

      button.addEventListener('click', async () => {
        if (isInert(button)) {
          return;
        }

        const following = button.dataset.pushState === STATE.subscribed;

        button.dataset.pushBusy = 'true';

        try {
          await (following
            ? unsubscribe(OneSignal, button, tag)
            : subscribe(OneSignal, button, tag));
        } finally {
          delete button.dataset.pushBusy;
        }
      });
    }
  });
}
